import { useState, useEffect } from 'react';

export interface ResponsiveLayoutState {
  isMobile: boolean;
  isLargeScreen: boolean;
}

/**
 * Hook to manage responsive layout state based on screen size
 * - isMobile: true when screen width <= 767px
 * - isLargeScreen: true when screen width >= 1024px
 */
export function useResponsiveLayout(): ResponsiveLayoutState {
  const [isMobile, setIsMobile] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mobileMql = window.matchMedia('(max-width: 767px)');
    const largeMql = window.matchMedia('(min-width: 1024px)');

    const sync = () => {
      setIsMobile(mobileMql.matches);
      setIsLargeScreen(largeMql.matches);
    };

    sync();

    const onMobileChange = () => sync();
    const onLargeChange = () => sync();

    if (mobileMql.addEventListener) {
      mobileMql.addEventListener('change', onMobileChange);
      largeMql.addEventListener('change', onLargeChange);
      return () => {
        mobileMql.removeEventListener('change', onMobileChange);
        largeMql.removeEventListener('change', onLargeChange);
      };
    }

    // Safari < 14 fallback
    (
      mobileMql as unknown as { addListener: (listener: () => void) => void }
    ).addListener(onMobileChange);
    (
      largeMql as unknown as { addListener: (listener: () => void) => void }
    ).addListener(onLargeChange);
    return () => {
      (
        mobileMql as unknown as {
          removeListener: (listener: () => void) => void;
        }
      ).removeListener(onMobileChange);
      (
        largeMql as unknown as {
          removeListener: (listener: () => void) => void;
        }
      ).removeListener(onLargeChange);
    };
  }, []);

  return { isMobile, isLargeScreen };
}
